import { subYears } from 'date-fns'
import { actions } from 'react-redux-form'
import { store } from '../../store/store'
import { DateFormatType, format } from '../../utils/date'
import { logAndReject } from '../../utils/promise'
import { getEntry } from '../entries/entry'
import { EntryKey } from '../entries/types'
import AccommodationData from '../models/accommodation'
import Country from '../models/country'
import FlightData from '../models/flight'
import { FunnelOrder } from '../models/funnelOrder'
import { getValueFromDestionation } from '../models/funnelOrder/destination'
import { IDiscountCode } from '../models/funnelOrder/discountCode'
import { getDriveDestinationString } from '../models/funnelOrder/driveDestination'
import { getRequestName } from '../models/funnelOrder/roomType'
import { Theme } from '../models/funnelOrder/theme'
import {
  getValueForIncomingTime,
  getValueForOutgoingTime,
} from '../models/funnelOrder/times'
import {
  getSiteCountryICC2,
  getSiteCurrencyISO,
  SiteCountry,
  SiteCurrency,
} from '../models/locale'
import { TripStep } from '../models/trip_info'
import {
  defaultImportantTripInfo,
  ImportantTripInfo,
} from '../models/unlockPage/important_trip_info'
import { getClient } from '../openapi'

export function getImportantOrderInfo(
  orderId: string,
): Promise<ImportantTripInfo> {
  return getClient()
    .then(client =>
      client.apis.bookings.bookings_order_important_info_read({ id: orderId }),
    )
    .then(data => data.body)
    .catch(() => defaultImportantTripInfo)
}

interface IRequestTraveller {
  email?: string
  do_notify?: boolean
  gender?: string
  first_name?: string
  last_name?: string
  call_name?: string
  birth_date: string | undefined
  extra_suitcase: boolean
  cancellation_insurance: boolean
  travel_insurance: boolean
}

interface IRequestBaby {
  gender?: string
  first_name?: string
  last_name?: string
  call_name?: string
  birth_date?: string
}

interface IRequestCrossSell {
  slug: string
  quantity?: number
}

function getData(
  funnelOrder: FunnelOrder,
  country: SiteCountry,
  currency: SiteCurrency,
) {
  const giftcardCodes = funnelOrder.giftcards.map(g => g.code)

  const travellers: IRequestTraveller[] = funnelOrder.travelers.map((t, i) => ({
    email: t.Email,
    do_notify: !t.DoNotNotify,
    gender: t.Gender.toLowerCase(),
    first_name: t.Forename,
    middle_name: t.Middlename,
    last_name: t.LastName,
    call_name: country === SiteCountry.be ? t.Forename : t.KnownAs,
    birth_date: t.DateOfBirth
      ? format(new Date(t.DateOfBirth), DateFormatType.apidate)
      : undefined,
    extra_suitcase: i < funnelOrder.numberOfBags,
    cancellation_insurance: funnelOrder.cancellationInsurance,
    travel_insurance: funnelOrder.travelInsurance,
    passport_number: t.TotmTraveler ? t.TotmTraveler.PassportNumber : '',
  }))

  const babies: IRequestBaby[] = funnelOrder.babies.map((b, i) => ({
    gender: b.Gender ? b.Gender.toLowerCase() : '',
    first_name: b.Forename,
    middle_name: b.Middlename,
    last_name: b.LastName,
    call_name: country === SiteCountry.be ? b.Forename : b.KnownAs,
    birth_date: b.DateOfBirth
      ? format(new Date(b.DateOfBirth), DateFormatType.apidate)
      : undefined,
  }))

  const co2CompensationExtra = {
    slug: `co2_compensation`,
    quantity: travellers.length,
  }
  const crossSelExtras: IRequestCrossSell[] = funnelOrder.co2Compensation
    ? [co2CompensationExtra]
    : []

  let data: { [name: string]: any } = {
    site_country: getSiteCountryICC2(country),
    site_currency: getSiteCurrencyISO(currency),

    nr_days: funnelOrder.days,
    room_type: getRequestName(funnelOrder.roomType),
    departure_date: format(
      new Date(funnelOrder.departureDate!),
      DateFormatType.apidate,
    ),
    theme: funnelOrder.theme.toString(),
    exclude_cities_ids: funnelOrder.excludedCitiesIds.map(x => x.id),
    include_cities_ids: funnelOrder.includedCitiesIds.map(x => x.id),
    excluded_cities_text: funnelOrder.excludedCities.join(', '),

    include_breakfast: funnelOrder.includeBreakfast,
    private_room: funnelOrder.privateRoom,
    co2_compensation: funnelOrder.co2Compensation,
    flexible_rebooking: funnelOrder.flexibleRebooking,
    bed_and_breakfast: funnelOrder.bedAndBreakfast,

    giftcard_codes: giftcardCodes,

    nr_pets: [Theme.drive, Theme.multiDrive].includes(funnelOrder.theme)
      ? funnelOrder.numberOfPets
      : 0,
    nr_baby_cribs: funnelOrder.babyCrib ? funnelOrder.numberOfBabies : 0,

    discount_code: funnelOrder.discountCode
      ? funnelOrder.discountCode.code
      : undefined,

    travellers,
    babies,
    cross_sell_extras: crossSelExtras,
  }

  if (
    funnelOrder.theme === Theme.drive ||
    funnelOrder.theme === Theme.train ||
    funnelOrder.theme === Theme.foodLoversDrive
  ) {
    data = {
      nr_destinations: 1,
      ...data,
    }

    if (funnelOrder.theme === Theme.train) {
      if (funnelOrder.trainTicket > 0) {
        crossSelExtras.push({
          slug: `trainticket`,
          quantity: funnelOrder.trainTicket,
        })
      }
      if (funnelOrder.trainTicket12 > 0) {
        crossSelExtras.push({
          slug: `trainticket12`,
          quantity: funnelOrder.trainTicket12,
        })
      }
    }
  } else if (
    funnelOrder.theme === Theme.multiFly ||
    funnelOrder.theme === Theme.multiDrive
  ) {
    const airports = new Map(funnelOrder.airports).get(country) || []
    data = {
      nr_destinations: funnelOrder.locations,
      ...data,
      theme: funnelOrder.theme === Theme.multiFly ? 'multi-city' : 'drive',
    }
    if (funnelOrder.theme === Theme.multiFly) {
      data.airport_bundle_ids = airports.map(airport => airport.id)
      data.to_flight_between = getValueForOutgoingTime(funnelOrder.outgoingTime)
      data.return_flight_between = getValueForIncomingTime(
        funnelOrder.incomingTime,
      )
    }
  } else if (funnelOrder.theme === Theme.solo) {
    const airports = new Map(funnelOrder.airports).get(country) || []
    data = {
      ...data,
      airport_bundle_ids: airports.map(airport => airport.id),
      to_flight_between: getValueForOutgoingTime(funnelOrder.outgoingTime),
      return_flight_between: getValueForIncomingTime(funnelOrder.incomingTime),
      theme: 'solo-together',
    }
  } else {
    const airports = new Map(funnelOrder.airports).get(country) || []
    data = {
      airport_bundle_ids: airports.map(airport => airport.id),
      ...data,
    }
    if (funnelOrder.theme === Theme.zonvoordeel) {
      data = {
        ...data,
        discount_code: 'zonvoordeel',
      }
    }
    if (funnelOrder.theme !== Theme.totm) {
      data = {
        direction: getValueFromDestionation(funnelOrder.destination),

        to_flight_between: getValueForOutgoingTime(funnelOrder.outgoingTime),
        return_flight_between: getValueForIncomingTime(
          funnelOrder.incomingTime,
        ),
        ...data,
      }
    }
  }

  if (funnelOrder.theme === Theme.totm) {
    return {
      ...data,
      nr_surf_easy: funnelOrder.totmOrder.surfLessonsEasy,
      nr_surf_hard: funnelOrder.totmOrder.surfLessonsHard,
    }
  }
  return data
}

export function getPlaceholderTravellers(funnelOrder: FunnelOrder) {
  // Replace traveller data with fake data that matches nrAdults & nrChildren so we can always get a price
  const adultBirthDate = subYears(new Date(), 25)
  const childBirthDate = subYears(new Date(), 10)
  const travellers = []
  for (
    let i = 0;
    i < funnelOrder.numberOfAdults + funnelOrder.numberOfChildren;
    i++
  ) {
    travellers.push({
      birth_date: format(
        i < funnelOrder.numberOfAdults ? adultBirthDate : childBirthDate,
        DateFormatType.apidate,
      ),
      extra_suitcase: i < funnelOrder.numberOfBags,
      cancellation_insurance: funnelOrder.cancellationInsurance,
      travel_insurance: funnelOrder.travelInsurance,
    })
  }
  return travellers
}

export function getNewPrice(funnelOrder: FunnelOrder) {
  const state = store.getState()
  return getCurrentTotal(
    funnelOrder,
    state.locale.country,
    state.locale.currency || SiteCurrency.euro,
  )
}

export function getDiscountCode(
  country: SiteCountry,
  code: string,
): Promise<IDiscountCode> {
  return getClient()
    .then(client =>
      client.apis.bookings.bookings_discount_code_read({
        site_country: getSiteCountryICC2(country),
        code,
      }),
    )
    .then(response => {
      return {
        title: response.body.title,
        description: response.body.description,
        amount: response.body.amount,
        code: response.body.code,
        isGift: response.body.is_gift,
      }
    })
}

interface CurrentTotalResponse {
  price: number
  wallet?: number
  orig_price?: number
}

export function getCurrentTotal(
  funnelOrder: FunnelOrder,
  country: SiteCountry,
  currency: SiteCurrency,
): Promise<CurrentTotalResponse> {
  const data = getData(funnelOrder, country, currency)
  data.travellers = getPlaceholderTravellers(funnelOrder)

  return getClient(true)
    .then(client => {
      const state = store.getState()
      return client.apis.bookings.bookings_total_price_get({
        data: {
          ...data,
          member_token: state.funnelOrder.useWallet
            ? state.member.token
            : undefined,
        },
      })
    })
    .then(response => response.body)
}

export function refreshDiscountCode(
  country: SiteCountry,
  currency: string,
  code: string,
): Promise<boolean> {
  return getDiscountCode(country, code).then(newDiscountCode => {
    store.dispatch(actions.change('funnelOrder.discountCode', newDiscountCode))
    return true
  })
}

export function refreshCurrentTotal() {
  const state = store.getState()
  return getCurrentTotal(
    state.funnelOrder,
    state.locale.country,
    state.locale.currency || SiteCurrency.euro,
  )
    .then(data => {
      store.dispatch(actions.change('funnelOrder.total', data.price))
      store.dispatch(actions.change('funnelOrder.wallet', data.wallet || 0))
      store.dispatch(
        actions.change('funnelOrder.originalTotal', data.orig_price || null),
      )
    })
    .catch(error => {
      store.dispatch(actions.change('funnelOrder.total', NaN))
      store.dispatch(actions.change('funnelOrder.originalTotal', null))
      const data = error.response.body
      if (data.hasOwnProperty('errors')) {
        store.dispatch(actions.change('ui.priceError', data.errors))
      }
    })
}

export interface IExtrasPrices {
  breakfast: number
  privateroom: number
  suitcase: number
  [slug: string]: number
}

export function getExtrasPrices(
  funnelOrder: FunnelOrder,
  country: SiteCountry,
  currency: SiteCurrency,
): Promise<IExtrasPrices> {
  const data = getData(funnelOrder, country, currency)

  data.travellers = getPlaceholderTravellers(funnelOrder)

  return getClient()
    .then(client => client.apis.bookings.bookings_cross_sell_read({ data }))
    .then(response => response.body)
}

export function createOrder(
  funnelOrder: FunnelOrder,
  country: SiteCountry,
  currency: SiteCurrency,
  memberToken?: string,
  landingPage?: string,
): Promise<{ paymentToken: string; amount: number }> {
  const mainBooker = funnelOrder.travelers[0]

  const driveData =
    funnelOrder.theme === Theme.drive
      ? {
          drive_destination: getDriveDestinationString(
            funnelOrder.driveDestination,
          ),
          license_plate: mainBooker.Car
            ? mainBooker.Car.LicensePlate
            : undefined,
          winter_tyres: mainBooker.Car ? mainBooker.Car.WinterTyres : undefined,
        }
      : {}

  const landingPageData =
    landingPage && landingPage !== ''
      ? {
          landing_page: landingPage.toLocaleLowerCase(),
        }
      : {}

  const entry = getEntry(funnelOrder.entry)
  const entryPageData =
    funnelOrder.entry !== EntryKey.none && entry
      ? {
          entry_page: entry.name.toLocaleLowerCase(),
        }
      : {}

  const data: any = {
    ...getData(funnelOrder, country, currency),
    ...{
      phone: mainBooker.Telephonenumber,
      info: funnelOrder.note.NoteText,
      zipcode: mainBooker.PostCode,
      street: mainBooker.Street,
      city: mainBooker.City,
      house_number: mainBooker.Housenumber,
      house_number_ext: mainBooker.Flatnumber,
      address_line_2: mainBooker.AddressLine2,
      country: getSiteCountryICC2(mainBooker.Country),
    },
    ...driveData,
    ...landingPageData,
    ...entryPageData,
  }
  return getClient(true)
    .then(client => {
      const state = store.getState()
      return client.apis.bookings.bookings_book_create({
        data: {
          ...data,
          member_token: state.funnelOrder.useWallet
            ? state.member.token
            : undefined,
        },
      })
    })
    .then(response => ({
      paymentToken: response.body.payment_token,
      amount: response.body.amount,
    }))
}

interface StepsData {
  id: number
  steps: TripStep[] | null
  short_url: string
  transportation: 'fly' | 'drive'
  travellers_number: number
}

export function getSteps(orderUrl: string, token: string): Promise<StepsData> {
  return getClient()
    .then(client =>
      client.apis.bookings.bookings_order_steps_read({ url: orderUrl, token }),
    )
    .then(response => response.body)
}

export interface HotelResponse {
  name: string
  address: string
  city: CityResponse
  latitude: string
  longitude: string
  checkin_str: string
  checkout_str: string
}

export interface CityResponse {
  name: string
  country: Country
  route_via_google_maps: boolean
}

export interface AccommodationResponse {
  hotel: HotelResponse
  checkin_date: Date
  checkout_date: Date
  reference: string
}

export function getAccommodationData(
  accommodationId: number,
  token: string,
): Promise<AccommodationData> {
  return getClient()
    .then(client =>
      client.apis.bookings.bookings_accommodation_read({
        id: accommodationId,
        token,
      }),
    )
    .then(response => {
      const data: AccommodationResponse = response.body
      return {
        hotel: {
          name: data.hotel.name,
          address: data.hotel.address,
          city: {
            name: data.hotel.city.name,
            country: data.hotel.city.country,
            routeViaGoogleMaps: data.hotel.city.route_via_google_maps,
          },
          latitude: data.hotel.latitude,
          longitude: data.hotel.longitude,
          checkinTime: data.hotel.checkin_str,
          checkoutTime: data.hotel.checkout_str,
        },
        checkinDate: data.checkin_date,
        checkoutDate: data.checkout_date,
        reference: data.reference,
      }
    })
}

export interface FlightResponse {
  id: number
  is_to: boolean
  flight_code: string
  airline: {
    name: string
    iata: string
    image: string
    app_store_id: string
    google_play_store_id: string
  }
  departure_utc: string
  departure_tz: string
  arrival_utc: string
  arrival_tz: string
  airport_to: {
    id: number
    iata: string
    name: string
  }
  airport_from: {
    id: number
    iata: string
    name: string
  }
  passengers: Array<{
    id: number
    first_name: string
    last_name: string
    birth_date: Date
  }>
  booking_code: string
}

export function getFlightData(
  flightId: number,
  token: string,
): Promise<FlightData> {
  return getClient()
    .then(client =>
      client.apis.bookings.bookings_flight_read({ id: flightId, token }),
    )
    .then(response => {
      const data: FlightResponse = response.body
      return {
        id: data.id,
        isToDestination: data.is_to,
        flightCode: data.flight_code,
        airline: {
          name: data.airline.name,
          code: data.airline.iata,
          image: data.airline.image,
          appStoreId: data.airline.app_store_id,
          googlePlayStoreId: data.airline.google_play_store_id,
        },
        departure: {
          date: new Date(data.departure_utc),
          tz: data.departure_tz,
          airport: {
            id: data.airport_from.id,
            name: data.airport_from.name,
            nameWithAirport: data.airport_to.name,
            code: data.airport_from.iata,
          },
        },
        arrival: {
          date: new Date(data.arrival_utc),
          tz: data.arrival_tz,
          airport: {
            id: data.airport_to.id,
            name: data.airport_to.name,
            nameWithAirport: data.airport_to.name,
            code: data.airport_to.iata,
          },
        },
        passengers: data.passengers.map(passenger => ({
          id: passenger.id,
          firstName: passenger.first_name,
          lastName: passenger.last_name,
          birthDate: passenger.birth_date,
        })),
        bookingCode: data.booking_code,
      }
    })
}

export function sendTripInformationEmail(
  orderId: number,
): Promise<{ sent: boolean }> {
  return getClient()
    .then(client =>
      client.apis.bookings.bookings_order_send_info_email_create({
        id: orderId,
        data: { id: orderId },
      }),
    )
    .then(data => data.body)
    .catch(logAndReject)
}

interface TravellDocsResponseFieldsValue {
  value: number | string | Date | null | undefined
}
export interface TravellDocsResponseFields {
  first_name?: TravellDocsResponseFieldsValue
  last_name?: TravellDocsResponseFieldsValue
  document_issue_date?: TravellDocsResponseFieldsValue
  document_valid_until?: TravellDocsResponseFieldsValue
  document_type?: TravellDocsResponseFieldsValue
  document_country?: TravellDocsResponseFieldsValue
  document_nationality?: TravellDocsResponseFieldsValue
  document_number?: TravellDocsResponseFieldsValue
}

export interface TravellDocsResponse {
  id: number
  fields: TravellDocsResponseFields
}

export function getTravellerDocs(
  orderId: number,
): Promise<TravellDocsResponse[]> {
  return getClient(true)
    .then(client => {
      return client.apis.bookings?.bookings_order_traveller_docs_read({
        id: orderId,
      })
    })
    .then(data => data.body)
    .catch(logAndReject)
}

export function putTravellerDocs(
  orderId: number,
  data: TravellDocsResponse[],
): Promise<any> {
  return getClient(true)
    .then(client => {
      return client.apis.bookings?.bookings_order_traveller_docs_update({
        id: orderId,
        data,
      })
    })
    .then(response => response.body)
    .catch(logAndReject)
}
